import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'bev_admin_token';

// sessionStorage clears on browser/tab close — reduces XSS blast radius vs localStorage.
// Note: for a hidden admin URL used by a single operator this is acceptable;
// upgrade to httpOnly cookies + CSRF token when adding more admin users.
const tokenStore = {
  get: () => window.sessionStorage.getItem(TOKEN_KEY),
  set: (v) => window.sessionStorage.setItem(TOKEN_KEY, v),
  clear: () => window.sessionStorage.removeItem(TOKEN_KEY),
};

const client = axios.create({ baseURL: API_BASE });

client.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-clear token on 401 so the admin re-logs in gracefully
client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401 && tokenStore.get()) {
      tokenStore.clear();
    }
    return Promise.reject(err);
  }
);

// -------- Public --------
export const bevApi = {
  listProducts: (params = {}) => client.get('/bev/products', { params }).then(r => r.data),
  getProduct: (id) => client.get(`/bev/products/${id}`).then(r => r.data),
  paypalConfig: () => client.get('/bev/paypal/config').then(r => r.data),
  createPaypalOrder: (product_id, coupon_code = null) => client.post('/bev/paypal/create-order', { product_id, coupon_code }).then(r => r.data),
  capturePaypalOrder: (paypal_order_id) => client.post(`/bev/paypal/capture-order/${paypal_order_id}`).then(r => r.data),
  createOrder: (payload) => client.post('/bev/orders', payload).then(r => r.data),
  createInquiry: (payload) => client.post('/bev/inquiries', payload).then(r => r.data),
  validateCoupon: (code, product_id) => client.post('/bev/coupons/validate', { code, product_id }).then(r => r.data),
};

// -------- Admin --------
export const bevAdminApi = {
  login: (email, password) => client.post('/bev/auth/login', { email, password }).then(r => r.data),
  me: () => client.get('/bev/auth/me').then(r => r.data),
  listProducts: () => client.get('/bev/products', { params: { include_hidden: true } }).then(r => r.data),
  createProduct: (payload) => client.post('/bev/products', payload).then(r => r.data),
  updateProduct: (id, payload) => client.patch(`/bev/products/${id}`, payload).then(r => r.data),
  deleteProduct: (id) => client.delete(`/bev/products/${id}`).then(r => r.data),
  listOrders: () => client.get('/bev/orders').then(r => r.data),
  listInquiries: () => client.get('/bev/inquiries').then(r => r.data),
  listCoupons: () => client.get('/bev/coupons').then(r => r.data),
  createCoupon: (payload) => client.post('/bev/coupons', payload).then(r => r.data),
  updateCoupon: (id, payload) => client.patch(`/bev/coupons/${id}`, payload).then(r => r.data),
  deleteCoupon: (id) => client.delete(`/bev/coupons/${id}`).then(r => r.data),
};

export const setAdminToken = (token) => tokenStore.set(token);
export const clearAdminToken = () => tokenStore.clear();
export const hasAdminToken = () => !!tokenStore.get();
